<template>
  <div>
    <Head title="Detalles de Cuenta" />

    <div class="max-w-7xl mx-auto py-10 sm:px-6 lg:px-8">
      <div class="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-8">
        <div>
          <nav class="flex mb-2" aria-label="Breadcrumb">
            <ol class="flex items-center space-x-2">
              <li><Link href="/admin" class="text-gray-400 hover:text-gray-500"><i class="fas fa-home" /></Link></li>
              <li><span class="text-gray-300">/</span></li>
              <li><Link href="/admin/accounts" class="text-gray-500 hover:text-gray-700 text-sm font-medium">Cuentas</Link></li>
              <li><span class="text-gray-300">/</span></li>
              <li class="text-gray-500 text-sm font-medium" aria-current="page">{{ account.name }}</li>
            </ol>
          </nav>
          <h1 class="text-2xl font-bold text-gray-900 flex items-center gap-3">
            {{ account.name }}
            <span
              class="text-sm font-normal px-2.5 py-0.5 rounded-full" 
              :class="account.hasActiveSubscription ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'"
            >
              {{ account.hasActiveSubscription ? 'Suscripción Activa' : 'Sin Suscripción' }}
            </span>
          </h1>
        </div>
        
        <div class="flex items-center gap-3">
          <button class="btn-secondary text-sm" @click="extendTrial">
            <i class="fas fa-calendar-plus mr-2 text-indigo-500" /> Extender Trial
          </button>
          <button class="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm font-medium transition-colors shadow-sm" @click="suspendAccount">
            <i class="fas fa-ban mr-2" /> Suspender Cuenta
          </button>
          <Link :href="`/admin/accounts`" class="btn-secondary">
            <i class="fas fa-arrow-left mr-2" /> Volver
          </Link>
          <button class="btn-primary" @click="showPlanModal = true">
            <i class="fas fa-crown mr-2" /> Cambiar Plan
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Main Info -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Account Details -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 class="text-lg font-bold text-gray-900 mb-4 border-b border-gray-100 pb-3">Información de la Cuenta</h3>
            <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6">
              <div>
                <dt class="text-xs text-gray-500 uppercase tracking-wider font-semibold">Razón Social / Nombre</dt>
                <dd class="mt-1 text-sm font-medium text-gray-900">{{ account.name }}</dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500 uppercase tracking-wider font-semibold">Email de Contacto</dt>
                <dd class="mt-1 text-sm font-medium text-gray-900">{{ account.email }}</dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500 uppercase tracking-wider font-semibold">Teléfono</dt>
                <dd class="mt-1 text-sm font-medium text-gray-900">{{ account.phone || '-' }}</dd>
              </div>
              <div>
                <dt class="text-xs text-gray-500 uppercase tracking-wider font-semibold">Identificación (RUT/DNI)</dt>
                <dd class="mt-1 text-sm font-medium text-gray-900">{{ account.identification || '-' }}</dd>
              </div>
              <div class="sm:col-span-2">
                <dt class="text-xs text-gray-500 uppercase tracking-wider font-semibold">Dirección</dt>
                <dd class="mt-1 text-sm font-medium text-gray-900">{{ account.address || '-' }}</dd>
              </div>
            </dl>
          </div>

          <!-- Users List -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div class="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h3 class="text-lg font-bold text-gray-900">Usuarios Asociados</h3>
              <span class="text-xs font-medium bg-white border border-gray-200 px-2 py-1 rounded-md text-gray-600">
                {{ account.users.length }} usuarios
              </span>
            </div>
            <div class="overflow-x-auto w-full">
              <table class="min-w-full divide-y divide-gray-100">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Usuario</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Rol</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Último Acceso</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                    <th class="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[150px]">Acciones</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-100">
                  <tr v-for="user in account.users" :key="user.id" class="hover:bg-gray-50 transition-colors">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex flex-col">
                        <span class="text-sm font-medium text-gray-900">{{ user.first_name }} {{ user.last_name }}</span>
                        <span class="text-xs text-gray-500">{{ user.email }}</span>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-50 text-blue-700 capitalize">
                        {{ user.role }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="text-sm text-gray-500">
                        {{ formatDate(user.last_login_at || user.updated_at) || 'N/A' }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span 
                        :class="user.active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'"
                        class="px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full"
                      >
                        {{ user.active ? 'Activo' : 'Inactivo' }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <Dropdown class="mt-1" placement="bottom-end">
                        <button type="button" class="flex items-center justify-end w-full text-gray-500 hover:text-gray-700 focus:outline-none transition-colors">
                          Acciones <i class="fas fa-chevron-down ml-2 text-xs" />
                        </button>
                        <template #dropdown>
                          <div class="mt-2 py-2 w-48 bg-white rounded-md shadow-xl border border-gray-100 z-50">
                            <button class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100" @click="impersonate(user)">
                              <i class="fas fa-user-secret w-5 text-center text-purple-500 mr-2" /> Iniciar sesión como
                            </button>
                            <button class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors" @click="resetPassword(user)">
                              <i class="fas fa-key w-5 text-center text-blue-500 mr-2" /> Restablecer Clave
                            </button>
                            <button class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors" @click="resendVerification(user)">
                              <i class="fas fa-envelope w-5 text-center text-amber-500 mr-2" /> Reenviar Correo
                            </button>
                            <button class="block w-full text-left px-4 py-2 text-sm transition-colors" :class="user.active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'" @click="toggleStatus(user)">
                              <i class="w-5 text-center mr-2" :class="user.active ? 'fas fa-ban text-red-500' : 'fas fa-check-circle text-green-500'" /> 
                              {{ user.active ? 'Bloquear Usuario' : 'Activar Usuario' }}
                            </button>
                          </div>
                        </template>
                      </Dropdown>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Organizations List -->
          <div v-if="account.organizations && account.organizations.length > 0" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mt-6">
            <div class="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h3 class="text-lg font-bold text-gray-900">Organizaciones / Equipos</h3>
              <span class="text-xs font-medium bg-white border border-gray-200 px-2 py-1 rounded-md text-gray-600">
                {{ account.organizations.length }} organizaciones
              </span>
            </div>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-100">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Nombre</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ciudad/Región</th>
                    <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Registro</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-100">
                  <tr v-for="org in account.organizations" :key="org.id" class="hover:bg-gray-50 transition-colors">
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span class="text-sm font-medium text-gray-900">{{ org.name }}</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ org.email || '-' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ org.city || '-' }}{{ org.region ? ', ' + org.region : '' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ formatDate(org.created_at) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Sidebar Info -->
        <div class="space-y-6">
          <!-- Plan Card -->
          <div class="bg-gradient-to-br from-orange-800 to-orange-900 text-white rounded-xl shadow-lg p-6 relative overflow-hidden">
            <!-- Decor -->
            <div class="absolute top-0 right-0 -mr-4 -mt-4 w-24 h-24 bg-white opacity-10 rounded-full blur-xl" />
                   
            <h3 class="text-sm font-medium text-slate-300 uppercase tracking-wider mb-2">Plan Actual</h3>
            <div class="flex items-center justify-between mb-4">
              <span class="text-3xl font-bold text-white capitalize">{{ getPlanName(account.plan) }}</span>
              <i class="fas fa-crown text-amber-400 text-2xl" />
            </div>
                   
            <div class="space-y-3 text-sm text-slate-300 mb-6">
              <div class="flex justify-between">
                <span>Expiración:</span>
                <span class="text-white font-medium">{{ formatDate(account.plan_expires_at) || 'N/A' }}</span>
              </div>
              <div class="flex justify-between">
                <span>Estado:</span>
                <span class="text-green-400 font-medium">Activo</span>
              </div>
            </div>

            <div class="flex flex-col gap-2">
              <button class="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold transition-all shadow-lg shadow-orange-500/20 active:scale-95" @click="showPlanModal = true">
                <i class="fas fa-sync-alt mr-2" /> Actualizar Plan
              </button>
              <button v-if="account.plan !== 'free'" class="w-full py-2.5 bg-white/10 hover:bg-rose-500 text-white border border-white/20 rounded-xl font-bold transition-all active:scale-95" @click="cancelAccountPlan">
                <i class="fas fa-times-circle mr-2 opacity-50 group-hover:opacity-100" /> Cancelar Suscripción
              </button>
            </div>
          </div>

          <!-- System Info -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 class="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Sistema</h3>
            <div class="space-y-3 text-sm">
              <div class="flex justify-between py-2 border-b border-gray-50">
                <span class="text-gray-500">ID Cuenta</span>
                <span class="font-mono text-gray-900">#{{ account.id }}</span>
              </div>
              <div class="flex justify-between py-2 border-b border-gray-50">
                <span class="text-gray-500">Registrado</span>
                <span class="font-medium text-gray-900">{{ formatDate(account.created_at) }}</span>
              </div>
              <div class="flex justify-between py-2">
                <span class="text-gray-500">Tipo Negocio</span>
                <span class="font-medium text-gray-900 capitalize">{{ account.business_type || 'General' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Update Plan Modal -->
    <div v-if="showPlanModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showPlanModal = false" />

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                <i class="fas fa-crown text-orange-600" />
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 id="modal-title" class="text-lg leading-6 font-medium text-gray-900">
                  Actualizar Plan de Suscripción
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500 mb-4">
                    Selecciona el nuevo plan para esta cuenta. Si es una actualización de pago, se generará una orden de venta automáticamente.
                  </p>
                                
                  <div class="grid grid-cols-1 gap-3">
                    <button 
                      v-for="plan in plans" 
                      :key="plan.id"
                      class="relative flex items-center justify-between p-4 border rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all text-left group"
                      :class="selectedPlan === plan.id ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200' : 'border-gray-200'"
                      @click="selectPlan(plan.id)"
                    >
                      <div>
                        <p class="font-bold text-gray-900 capitalize">{{ getPlanName(plan.id) }}</p>
                        <p class="text-xs text-gray-500">{{ plan.price > 0 ? `$${plan.price}/mes` : 'Gratis' }}</p>
                      </div>
                      <div v-if="selectedPlan === plan.id" class="text-orange-600">
                        <i class="fas fa-check-circle" />
                      </div>
                    </button>
                  </div>

                  <!-- Fecha y Frecuencia -->
                  <div class="mt-6 pt-6 border-t border-gray-100">
                    <label class="block text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Vencimiento del Plan</label>
                                     
                    <div class="flex items-center gap-2 mb-4">
                      <button type="button" class="flex-1 py-2 px-4 rounded-xl border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-50 active:scale-95 transition-all" @click="setFrequency('month')">
                        <i class="fas fa-calendar-alt mr-2 text-orange-500" /> +1 Mes
                      </button>
                      <button type="button" class="flex-1 py-2 px-4 rounded-xl border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-50 active:scale-95 transition-all" @click="setFrequency('year')">
                        <i class="fas fa-star mr-2 text-amber-500" /> +1 Año
                      </button>
                    </div>

                    <input 
                      v-model="newExpiresAt" 
                      type="date"
                      class="w-full bg-gray-50 border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button 
              :disabled="processing" 
              type="button"
              class="w-full inline-flex justify-center rounded-xl border border-transparent shadow-lg px-6 py-2.5 bg-orange-600 text-sm font-black uppercase tracking-widest text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto disabled:opacity-50 transition-all shadow-orange-600/20" 
              @click="updatePlan"
            >
              {{ processing ? 'Procesando...' : 'Guardar Cambios' }}
            </button>
            <button 
              type="button" 
              class="mt-3 w-full inline-flex justify-center rounded-xl border border-gray-300 shadow-sm px-6 py-2.5 bg-white text-sm font-bold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto transition-all" 
              @click="showPlanModal = false"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Dropdown from '@/Shared/Dropdown'

export default {
  name: 'AccountShow',

  components: {
    Head,
    Link,
    Dropdown,
  },

  layout: Layout,

  props: {
    account: Object,
  },

  data() {
    return {
      showPlanModal: false,
      selectedPlan: this.account.plan,
      newExpiresAt: this.account.plan_expires_at ? this.account.plan_expires_at.split('T')[0] : null,
      processing: false,
      plans: [
        { id: 'free', name: 'Gratis', price: 0 },
        { id: 'entrepreneur', name: 'Emprendedor', price: 15 },
        { id: 'sme', name: 'Pymes', price: 30 },
        { id: 'enterprise', name: 'Empresas', price: 60 },
      ],
    }
  },

  methods: {
    getPlanName(plan) {
      const plans = {
        free: 'Gratis',
        entrepreneur: 'Emprendedor',
        sme: 'Pymes',
        enterprise: 'Empresas',
      }
      return plans[plan] || plan
    },

    formatDate(date) {
      if(!date) return null
      return new Date(date).toLocaleDateString('es-VE', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric', 
      })
    },

    selectPlan(planId) {
      this.selectedPlan = planId
    },

    setFrequency(type) {
      const date = new Date()
      if (type === 'month') {
        date.setMonth(date.getMonth() + 1)
      } else if (type === 'year') {
        date.setFullYear(date.getFullYear() + 1)
      }
      this.newExpiresAt = date.toISOString().split('T')[0]
    },

    cancelAccountPlan() {
      if(confirm('¿Estás seguro de que deseas cancelar la suscripción de esta cuenta? Se bajará al plan Gratuito de forma inmediata.')) {
        this.processing = true
        this.$inertia.put(`/admin/accounts/${this.account.id}/plan`, {
          plan: 'free',
          plan_expires_at: null,
        }, {
          onSuccess: () => {
            this.processing = false
          },
          onError: () => {
            this.processing = false
          },
        })
      }
    },

    updatePlan() {
      this.processing = true
      this.$inertia.put(`/admin/accounts/${this.account.id}/plan`, {
        plan: this.selectedPlan,
        plan_expires_at: this.newExpiresAt,
      }, {
        onSuccess: () => {
          this.showPlanModal = false
          this.processing = false
        },
        onError: () => {
          this.processing = false
        },
      })
    },

    extendTrial() {
      if(confirm('¿Estás seguro de extender el periodo de prueba por 15 días adicionales?')) {
        this.$inertia.post(`/admin/accounts/${this.account.id}/extend-trial`)
      }
    },
    suspendAccount() {
      if(confirm('¿Estás seguro de suspender esta cuenta? Sus usuarios no podrán acceder.')) {
        this.$inertia.post(`/admin/accounts/${this.account.id}/suspend`)
      }
    },
    resetPassword(user) {
      if(confirm(`¿Estás seguro de restablecer la contraseña para el usuario ${user.email}?`)) {
        this.$inertia.post(`/admin/users/${user.id}/reset-password`)
      }
    },
    resendVerification(user) {
      if(confirm(`¿Reenviar correo de verificación a ${user.email}?`)) {
        this.$inertia.post(`/admin/users/${user.id}/resend-verification`)
      }
    },
    impersonate(user) {
      if(confirm(`¿Estás seguro de querer iniciar sesión como ${user.first_name}?`)) {
        window.location.href = `/admin/impersonate/${user.id}`
      }
    },
    toggleStatus(user) {
      const action = user.active ? 'desactivar' : 'activar'
      if(confirm(`¿Estás seguro de ${action} al usuario ${user.email}?`)) {
        this.$inertia.post(`/admin/users/${user.id}/toggle-status`)
      }
    },
  },
}
</script>